import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createEvaluation,
  createSupplier,
  deactivateSupplier,
  fetchEvaluations,
  fetchSupplier,
  fetchSuppliers,
  updateSupplier,
} from './suppliersApi';
import type {
  Pagination,
  Supplier,
  SupplierEvaluation,
  SupplierEvaluationPayload,
  SupplierListParams,
  SupplierListResponse,
  SupplierPayload,
  SupplierStatus,
} from './types';

interface SuppliersState {
  items: Supplier[];
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  search: string;
  filterStatus: SupplierStatus | '';
  sortBy: 'legalName' | 'category' | 'status' | 'createdAt';
  sortDir: 'asc' | 'desc';
  current: Supplier | null;
  evaluations: SupplierEvaluation[];
  evalsLoading: boolean;
}

const initialState: SuppliersState = {
  items: [],
  loading: false,
  error: null,
  pagination: { page: 1, pageSize: 20, total: 0, pages: 0 },
  search: '',
  filterStatus: '',
  sortBy: 'createdAt',
  sortDir: 'desc',
  current: null,
  evaluations: [],
  evalsLoading: false,
};

export const loadSuppliers = createAsyncThunk<SupplierListResponse, SupplierListParams | undefined>(
  'suppliers/load',
  async (params) => fetchSuppliers(params ?? {}),
);

export const loadSupplier = createAsyncThunk<Supplier, string>(
  'suppliers/loadOne',
  async (id) => fetchSupplier(id),
);

export const createSupplierThunk = createAsyncThunk<Supplier, SupplierPayload>(
  'suppliers/create',
  async (payload) => createSupplier(payload),
);

export const updateSupplierThunk = createAsyncThunk<Supplier, { id: string; payload: SupplierPayload }>(
  'suppliers/update',
  async ({ id, payload }) => updateSupplier(id, payload),
);

export const deactivateSupplierThunk = createAsyncThunk<Supplier, string>(
  'suppliers/deactivate',
  async (id) => deactivateSupplier(id),
);

export const loadEvaluations = createAsyncThunk<SupplierEvaluation[], string>(
  'suppliers/loadEvaluations',
  async (supplierId) => fetchEvaluations(supplierId),
);

export const createEvaluationThunk = createAsyncThunk<
  SupplierEvaluation,
  { supplierId: string; payload: SupplierEvaluationPayload }
>('suppliers/createEvaluation', async ({ supplierId, payload }) => createEvaluation(supplierId, payload));

const slice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    setSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setFilterStatus(state, action: PayloadAction<SupplierStatus | ''>) {
      state.filterStatus = action.payload;
    },
    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.pagination.pageSize = action.payload;
    },
    clearCurrent(state) {
      state.current = null;
      state.evaluations = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(loadSuppliers.rejected, (state) => {
        state.loading = false;
        state.error = 'No se pudo cargar la lista de proveedores';
      })
      .addCase(loadSupplier.fulfilled, (state, action) => {
        state.current = action.payload;
      })
      .addCase(createSupplierThunk.fulfilled, (state, action) => {
        state.items = [action.payload, ...state.items];
        state.pagination.total += 1;
      })
      .addCase(updateSupplierThunk.fulfilled, (state, action) => {
        state.items = state.items.map((s) => (s.id === action.payload.id ? action.payload : s));
        if (state.current?.id === action.payload.id) state.current = { ...state.current, ...action.payload };
      })
      .addCase(deactivateSupplierThunk.fulfilled, (state, action) => {
        state.items = state.items.map((s) => (s.id === action.payload.id ? action.payload : s));
      })
      .addCase(loadEvaluations.pending, (state) => {
        state.evalsLoading = true;
      })
      .addCase(loadEvaluations.fulfilled, (state, action) => {
        state.evalsLoading = false;
        state.evaluations = action.payload;
      })
      .addCase(loadEvaluations.rejected, (state) => {
        state.evalsLoading = false;
      })
      .addCase(createEvaluationThunk.fulfilled, (state, action) => {
        state.evaluations = [action.payload, ...state.evaluations];
      });
  },
});

export const { setSearch, setFilterStatus, setPage, setPageSize, clearCurrent } = slice.actions;
export default slice.reducer;
